import type { UserNotification } from '../../contexts/NotificationContext';
import { crmContactPath } from '../../lib/crm-v2/contactRoutes';

/**
 * Where clicking a notification in the bell should navigate, or null to stay put.
 *
 * Order matters and mirrors the original inline logic: the typed contact
 * notifications first, then an explicit app-relative `data.path`, then an
 * app-relative `data.link`, then — for any other type (email_reply, lead_reaped,
 * aria_claim, …) — the contact when a `contactId` is present.
 */
const isAppRelative = (value: unknown): value is string =>
  typeof value === 'string' && value.startsWith('/') && !value.startsWith('//');

export function getNotificationTarget(notification: Pick<UserNotification, 'type' | 'data'>): string | null {
  const data = notification.data;
  const contactId = typeof data?.contactId === 'string' && data.contactId ? data.contactId : null;

  if (
    contactId &&
    (notification.type === 'whatsapp_reply' ||
      notification.type === 'call_centre_disposition' ||
      notification.type === 'new_lead_assigned')
  ) {
    return crmContactPath(contactId);
  }
  if (isAppRelative(data?.path)) {
    // Support (and future) notifications carry their own app-relative deep link
    return data.path;
  }
  if (isAppRelative(data?.link)) {
    // e.g. consultant_review_available → /consultant/monthly-review. Absolute links
    // (contactLink() builds https://…) are not followed; those fall back to contactId.
    return data.link;
  }
  if (contactId) {
    return crmContactPath(contactId);
  }
  return null;
}
